import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMobileEdgeSwipeBack } from '../../hooks/useMobileEdgeSwipeBack';
import { AnimatePresence, motion, Variants } from 'framer-motion';
import { useDashboardStore } from '../../store/dashboardStore';
import { Button } from '../ui/button';

type CalendarCell = {
  date: Date;
  isCurrentMonth: boolean;
  isToday: boolean;
};

function startOfDay(date: Date): Date {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

function areSameDate(a?: Date | null, b?: Date | null): boolean {
  if (!a || !b) return false;
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function formatYYYYMMDD(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function buildMonthMatrix(viewYear: number, viewMonthIndex0: number): CalendarCell[] {
  // viewMonthIndex0: 0-11
  const firstOfMonth = new Date(viewYear, viewMonthIndex0, 1);
  const firstOfMonthDay = firstOfMonth.getDay(); // 0:Sun
  const startDate = new Date(firstOfMonth);
  startDate.setDate(1 - firstOfMonthDay); // start from Sunday of the first grid week

  const today = startOfDay(new Date());
  const cells: CalendarCell[] = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(startDate);
    d.setDate(startDate.getDate() + i);
    const isCurrentMonth = d.getMonth() === viewMonthIndex0;
    cells.push({
      date: d,
      isCurrentMonth,
      isToday: areSameDate(d, today),
    });
  }
  return cells;
}

interface MonthCalendarPanelProps {
  context?: 'settings' | 'workflow';
  isMobile?: boolean;
}

type MonthSlideDir = 'forward' | 'back';

/** PC: 月グリッドの横スライド。スマホ: 横移動なし（フェードのみ）。横スライドとタップ後の薄グレーが重なると隣の日へ色が移るように見えるため。 */
const monthGridSlideVariantsDesktop: Variants = {
  initial: (dir: MonthSlideDir) => ({
    x: dir === 'forward' ? '14%' : '-14%',
    opacity: 0,
  }),
  animate: { x: 0, opacity: 1 },
  exit: (dir: MonthSlideDir) => ({
    x: dir === 'forward' ? '-14%' : '14%',
    opacity: 0,
  }),
};

const monthGridVariantsMobile: Variants = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 },
};

export const MonthCalendarPanel: React.FC<MonthCalendarPanelProps> = ({ context = 'settings', isMobile = false }) => {
  const {
    currentUser,
    currentClinicId,
    setSelectedDate,
    setShowSchedulePanel,
    setSchedulePanelSource,
    scheduleSelectedStaffId,
    setShowSchedulePatientListPanel,
    setShowSettingsDatePanel,
    isNavigatingBack,
    setIsNavigatingBack,
    navigateBackToWorkflowStaffList,
    navigateBackToStaffList,
    showSchedulePanel,
    workflowRootRevealFromSettings,
    selectedDate,
  } = useDashboardStore();

  // 通常スタッフはスタッフ一覧に戻れない（他スタッフの予約を見せないため）
  const canNavigateBack = context !== 'workflow' || currentUser?.role === 'admin' || currentUser?.role === 'developer';

  const today = new Date();
  /** 診療：スケジュールから戻って再マウントしたとき、選択日の月を保つ（設定は従来どおり今日基準） */
  const initialMonthDate =
    context === 'workflow' && selectedDate ? startOfDay(selectedDate) : today;
  const [viewYear, setViewYear] = useState(() => initialMonthDate.getFullYear());
  const [viewMonthIndex0, setViewMonthIndex0] = useState(() => initialMonthDate.getMonth());
  const [monthSlideDir, setMonthSlideDir] = useState<MonthSlideDir>('forward');

  const cells = useMemo(() => buildMonthMatrix(viewYear, viewMonthIndex0), [viewYear, viewMonthIndex0]);
  const [bookedYmdSet, setBookedYmdSet] = useState<Set<string>>(() => new Set());
  const bookedFetchSeq = useRef(0);
  /** スマホ：グリッド上の横スワイプで月送り（縦スクロールは判別して無視） */
  const monthSwipeStartRef = useRef<{ x: number; y: number } | null>(null);

  const fetchBookedDatesForGrid = useCallback(async () => {
    if (!scheduleSelectedStaffId) {
      setBookedYmdSet(new Set());
      return;
    }
    const seq = ++bookedFetchSeq.current;
    const matrix = buildMonthMatrix(viewYear, viewMonthIndex0);
    const from = formatYYYYMMDD(matrix[0].date);
    const to = formatYYYYMMDD(matrix[41].date);
    try {
      const { getBookedAppointmentDatesInRange } = await import('../../lib/api');
      const list = await getBookedAppointmentDatesInRange(
        from,
        to,
        scheduleSelectedStaffId,
        currentClinicId ?? undefined
      );
      if (seq !== bookedFetchSeq.current) return;
      setBookedYmdSet(new Set(list));
    } catch {
      if (seq !== bookedFetchSeq.current) return;
      setBookedYmdSet(new Set());
    }
  }, [viewYear, viewMonthIndex0, scheduleSelectedStaffId, currentClinicId]);

  useEffect(() => {
    void fetchBookedDatesForGrid();
  }, [fetchBookedDatesForGrid]);

  /** 予約テーブル変更時にドットを自動更新（同一タブ内の登録・キャンセル含む） */
  useEffect(() => {
    let unsub: (() => void) | undefined;
    let cancelled = false;
    import('../../lib/api').then(({ subscribeAppointments }) => {
      if (cancelled) return;
      unsub = subscribeAppointments(() => {
        void fetchBookedDatesForGrid();
      }, currentClinicId ?? undefined);
    });
    return () => {
      cancelled = true;
      unsub?.();
    };
  }, [currentClinicId, fetchBookedDatesForGrid]);

  const goToMonth = (y: number, m0: number) => {
    if (y === viewYear && m0 === viewMonthIndex0) return;
    const curOrd = viewYear * 12 + viewMonthIndex0;
    const nextOrd = y * 12 + m0;
    setMonthSlideDir(nextOrd > curOrd ? 'forward' : 'back');
    setViewYear(y);
    setViewMonthIndex0(m0);
  };

  const handlePrevMonth = () => {
    const prev = new Date(viewYear, viewMonthIndex0 - 1, 1);
    goToMonth(prev.getFullYear(), prev.getMonth());
  };

  const handleNextMonth = () => {
    const next = new Date(viewYear, viewMonthIndex0 + 1, 1);
    goToMonth(next.getFullYear(), next.getMonth());
  };
  const handleResetThisMonth = () => {
    const cur = new Date();
    goToMonth(cur.getFullYear(), cur.getMonth());
    // 選択日は変更しない（スケジュールを開かせない）
    setShowSchedulePanel(false);
  };

  const handleMonthGridTouchStart = (e: React.TouchEvent) => {
    if (!isMobile) return;
    const t = e.touches[0];
    if (!t) return;
    monthSwipeStartRef.current = { x: t.clientX, y: t.clientY };
  };

  const handleMonthGridTouchEnd = (e: React.TouchEvent) => {
    if (!isMobile) return;
    const start = monthSwipeStartRef.current;
    monthSwipeStartRef.current = null;
    if (!start) return;
    const t = e.changedTouches[0];
    if (!t) return;
    const dx = t.clientX - start.x;
    const dy = t.clientY - start.y;
    const absX = Math.abs(dx);
    const absY = Math.abs(dy);
    const minSwipe = 56;
    if (absX < minSwipe) return;
    if (absX < absY * 1.35) return;
    if (dx < 0) {
      handleNextMonth();
    } else {
      handlePrevMonth();
    }
  };

  const handleMonthGridTouchCancel = () => {
    monthSwipeStartRef.current = null;
  };

  const panelVariants: Variants = isMobile 
    ? {
        hidden: { x: '100%', opacity: 1 },
        hiddenBack: { x: '-100%', opacity: 1 },
        visible: { x: 0, opacity: 1 },
      }
    : {
        hidden: { x: '-100%' },  // デスクトップ版は左から
        visible: { x: 0 },
      };

  const youbi = ['日', '月', '火', '水', '木', '金', '土'];

  const workflowCalendarObscured = !!(isMobile && context === 'workflow' && showSchedulePanel);
  const instantWorkflowRoot = isMobile && context === 'workflow' && workflowRootRevealFromSettings;
  const monthSwipeRef = useRef<HTMLDivElement>(null);
  const handleMonthEdgeBack = useCallback(() => {
    if (!canNavigateBack) return;
    if (context === 'workflow') {
      navigateBackToWorkflowStaffList();
    } else {
      navigateBackToStaffList();
    }
  }, [canNavigateBack, context, navigateBackToWorkflowStaffList, navigateBackToStaffList]);

  /** 通常スタッフは「＜」が出ないが、左端スワイプだけ有効なと Safari 履歴戻りが走るため常にリスナーを付ける（閾値超え時は canNavigateBack のときだけ遷移） */
  const { swipeStyle: monthSwipeStyle } = useMobileEdgeSwipeBack(monthSwipeRef, {
    isMobile: !!isMobile,
    enabled: !!isMobile && !workflowCalendarObscured,
    onNavigateBack: handleMonthEdgeBack,
  });

  return (
    <div
      ref={monthSwipeRef}
      style={isMobile ? monthSwipeStyle : undefined}
      className={
        isMobile
          ? `fixed top-0 left-0 right-0 bottom-0 pt-[var(--header-actual,var(--header-total))] ${context === 'workflow' ? 'z-[41]' : 'z-[43]'}`
          : 'contents'
      }
    >
    <motion.div
      variants={panelVariants}
      initial={
        instantWorkflowRoot ? 'visible' : isMobile && isNavigatingBack ? 'hiddenBack' : 'hidden'
      }
      animate="visible"
      exit={
        isMobile
          ? isNavigatingBack
            ? { x: '100%', opacity: 0, transition: { duration: 0.35, ease: 'easeOut' } }
            : { x: '-100%' }
          : undefined
      }
      transition={instantWorkflowRoot ? { duration: 0 } : { duration: 0.3, ease: 'easeOut' }}
      className={isMobile 
        ? `h-full w-full bg-white flex flex-col shadow-lg${workflowCalendarObscured ? ' pointer-events-none' : ''}` 
        : `panel-col ${context === 'workflow' ? 'panel-col--workflow' : 'panel-col--settings'} z-10 bg-white border-r flex flex-col shadow-lg`
      }
    >
      <div className="p-4 border-b flex items-center justify-between">
        <div className="flex items-center space-x-2">
          {isMobile ? (
            canNavigateBack ? (
              <button
                onClick={() => {
                  if (context === 'workflow') {
                    navigateBackToWorkflowStaffList();
                  } else {
                    navigateBackToStaffList();
                  }
                }}
                className="text-lg font-semibold text-gray-800 hover:text-gray-600 transition-colors cursor-pointer"
              >
                月カレンダー
              </button>
            ) : (
              <h2 className="text-lg font-semibold text-gray-800">月カレンダー</h2>
            )
          ) : (
            <h2 className="text-lg font-semibold text-gray-800">月カレンダー</h2>
          )}
        </div>
      </div>

      <div className={`flex-1 overflow-y-auto ${isMobile ? 'pb-[var(--bottom-menu-total)]' : ''}`}>
        <div className="p-3">
          {/* 月切替（1行・中央配置） */}
          <div className="flex items-center justify-center gap-3 mb-3 text-sm">
            <button
              aria-label="前月"
              className={
                isMobile
                  ? 'px-3 py-1 text-gray-700 rounded touch-manipulation [-webkit-tap-highlight-color:transparent] [@media(hover:hover)]:hover:bg-gray-100 active:bg-gray-100'
                  : 'px-3 py-1 text-gray-700 hover:bg-gray-100 rounded'
              }
              onClick={(e) => {
                handlePrevMonth();
                (e.currentTarget as HTMLButtonElement).blur();
              }}
            >
              ＜
            </button>
            <div className="flex items-center gap-2">
              <select
                className="border border-gray-200 rounded px-2 py-1 text-gray-700 text-base font-bold"
                value={viewYear}
                onChange={(e) => goToMonth(parseInt(e.target.value, 10), viewMonthIndex0)}
              >
                {Array.from({ length: 11 }).map((_, i) => {
                  const y = today.getFullYear() - 5 + i;
                  return (
                    <option key={y} value={y}>{y}年</option>
                  );
                })}
              </select>
              <select
                className="border border-gray-200 rounded px-2 py-1 text-gray-700 text-base font-bold"
                value={viewMonthIndex0}
                onChange={(e) => goToMonth(viewYear, parseInt(e.target.value, 10))}
              >
                {Array.from({ length: 12 }).map((_, i) => (
                  <option key={i} value={i}>{(i + 1).toString().padStart(2, '0')}月</option>
                ))}
              </select>
            </div>
            <button
              aria-label="次月"
              className={
                isMobile
                  ? 'px-3 py-1 text-gray-700 rounded touch-manipulation [-webkit-tap-highlight-color:transparent] [@media(hover:hover)]:hover:bg-gray-100 active:bg-gray-100'
                  : 'px-3 py-1 text-gray-700 hover:bg-gray-100 rounded'
              }
              onClick={(e) => {
                handleNextMonth();
                (e.currentTarget as HTMLButtonElement).blur();
              }}
            >
              ＞
            </button>
          </div>
          {/* 曜日＋グリッド：スマホは横スワイプで前月／翌月（touch-pan-y で縦スクロール優先） */}
          <div
            className={isMobile ? 'touch-pan-y overflow-x-hidden' : 'overflow-x-hidden'}
            onTouchStart={isMobile ? handleMonthGridTouchStart : undefined}
            onTouchEnd={isMobile ? handleMonthGridTouchEnd : undefined}
            onTouchCancel={isMobile ? handleMonthGridTouchCancel : undefined}
          >
            <AnimatePresence mode="wait" initial={false}>
              <motion.div
                key={`${viewYear}-${viewMonthIndex0}`}
                custom={isMobile ? undefined : monthSlideDir}
                variants={isMobile ? monthGridVariantsMobile : monthGridSlideVariantsDesktop}
                initial="initial"
                animate="animate"
                exit="exit"
                transition={{
                  duration: isMobile ? 0.14 : 0.24,
                  ease: [0.25, 0.1, 0.25, 1],
                }}
              >
                {/* 曜日ヘッダー */}
                <div className="grid grid-cols-7 gap-1 mb-2 text-center text-xs text-gray-500">
                  {youbi.map((w) => (
                    <div key={w}>{w}</div>
                  ))}
                </div>
                {/* 日付グリッド */}
                <div className="grid grid-cols-7 gap-1">
                  {cells.map((cell) => {
                    // 選択日はストアのまま（スケジュール連携用）だが、セルには薄緑ハイライトを付けない（戻遷移後の紛らわしさ防止）
                    const hasBooking = bookedYmdSet.has(formatYYYYMMDD(cell.date));
                    const ymdKey = formatYYYYMMDD(cell.date);
                    const baseClasses =
                      'relative aspect-square rounded-md flex flex-col items-center justify-center text-sm transition-colors border border-transparent focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-300 touch-manipulation [-webkit-tap-highlight-color:transparent]';
                    const hover =
                      isMobile
                        ? ' [@media(hover:hover)]:hover:bg-gray-100 [@media(hover:hover)]:hover:shadow-sm active:bg-gray-100 active:shadow-sm'
                        : ' hover:bg-gray-100 hover:shadow-sm';
                    const monthTone = cell.isCurrentMonth ? ' text-gray-800' : ' text-gray-400 opacity-60';
                    // ring-inset: 親の overflow-x-hidden でも枠が切れない（今日マーク）
                    const todayRing = cell.isToday ? ' ring-2 ring-inset ring-[#00a48d]' : '';
                    return (
                      <button
                        key={ymdKey}
                        type="button"
                        onClick={(e) => {
                          // 進む動作なので、isNavigatingBackをfalseにリセット
                          setIsNavigatingBack(false);
                          // 設定レーンの予約では、日付確定時点でソースを明示（store の分岐とズレても空き枠が押せるようにする）
                          if (context === 'settings') {
                            setSchedulePanelSource('settings');
                          }
                          setSelectedDate(startOfDay(cell.date));
                          // 設定フロー用：スマホ版でのみ月カレンダーパネルを閉じる（PC版では閉じずに右側に展開）
                          if (context === 'settings' && isMobile) {
                            setShowSettingsDatePanel(false);
                          }
                          // 診療フロー用：日付変更時は常に「申し送り（詳細）」と予約登録パネルを閉じる
                          if (context === 'workflow') {
                            setShowSchedulePatientListPanel(false);
                            useDashboardStore.setState((state) => ({
                              showDetailPanel: false,
                              selectedPatientId: null,
                              detailPanelRevision: state.detailPanelRevision + 1,
                            }));

                            // スタッフが選択済みなら、スケジュールを表示
                            if (scheduleSelectedStaffId) {
                              // スマホ：設定フローと同様、月カレをいったん閉じてからスケジュールを開く（固定 z で重ねると他パネルと挙動が揃わない）
                              if (isMobile) {
                                useDashboardStore.setState({
                                  schedulePanelSource: 'workflow',
                                  showDatePanel: false,
                                  showSchedulePanel: true,
                                });
                              } else {
                                setSchedulePanelSource('workflow');
                                setShowSchedulePanel(true);
                              }
                            }
                          }
                          (e.currentTarget as HTMLButtonElement).blur();
                        }}
                        className={baseClasses + monthTone + hover + todayRing}
                        aria-current={cell.isToday ? 'date' : undefined}
                      >
                        <div className={cell.isToday ? 'font-bold' : ''}>{cell.date.getDate()}</div>
                        {hasBooking && (
                          <span
                            className="absolute bottom-1 left-1/2 h-1.5 w-1.5 -translate-x-1/2 rounded-full bg-red-500"
                            aria-hidden
                          />
                        )}
                      </button>
                    );
                  })}
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
          {/* 今月へ戻す（今月以外のときのみ、ふわっと表示） */}
          {!(viewYear === today.getFullYear() && viewMonthIndex0 === today.getMonth()) && (
            <motion.div
              className="mt-3 px-3"
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2, ease: 'easeOut' }}
            >
              <Button type="button" variant="ctaMuted" className="w-full" onClick={handleResetThisMonth}>
                リセット
              </Button>
            </motion.div>
          )}
        </div>
      </div>
    </motion.div>
    </div>
  );
};


